import { supabase } from "@/integrations/supabase/client";
import { 
  CreateDiscrepancyInput, 
  UpdateDiscrepancyInput, 
  DiscrepancyStatus,
  ProcurementDiscrepancy 
} from "@/types/database/procurement-discrepancies";
import { sendTelegramNotification } from "@/services/telegramService";
import { resolveSourceNames, getSourceName } from "@/services/discrepancySourceResolver";

const DISCREPANCY_THRESHOLD = 1; // Minimum difference in Birr to create a discrepancy

/**
 * Calculate the final net payable amount for a procurement using item-level data
 */
export async function calculateFinalNetPayable(procurementId: string): Promise<number> {
  // First try item-level calculation
  const { data: items, error: itemsError } = await supabase
    .from('procurement_items')
    .select(`
      id,
      price_per_unit,
      procurement_item_warehouse (
        received_quantity_kg,
        net_payable_kg
      ),
      procurement_item_quality (
        compensation_kg
      )
    `)
    .eq('procurement_id', procurementId);

  if (itemsError) {
    console.error('[DISCREPANCY] Error fetching procurement items:', itemsError);
  }

  // If we have item-level data with warehouse info, use that
  if (items && items.length > 0) {
    let hasItemLevelData = false;
    let totalNetPayable = 0;

    for (const item of items) {
      const warehouseData = item.procurement_item_warehouse?.[0];
      const qualityData = item.procurement_item_quality?.[0];
      
      if (warehouseData?.received_quantity_kg !== undefined) {
        hasItemLevelData = true;
        const receivedKg = warehouseData.received_quantity_kg || 0;
        const compensationKg = qualityData?.compensation_kg || 0;
        const netPayableKg = receivedKg - compensationKg;
        const pricePerUnit = item.price_per_unit || 0;
        totalNetPayable += netPayableKg * pricePerUnit;
      }
    }

    if (hasItemLevelData) {
      console.log('[DISCREPANCY] Calculated from item-level data:', totalNetPayable);
      return totalNetPayable;
    }
  }

  // Fallback to procurement-level calculation
  const { data: procurement, error: procError } = await supabase
    .from('procurements')
    .select(`
      total_quantity,
      total_amount,
      procurement_warehouse (
        received_quantity_kg,
        net_kg_payable
      ),
      procurement_quality (
        compensation_kg,
        preliminary_compensation_kg
      )
    `)
    .eq('id', procurementId)
    .single();

  if (procError || !procurement) {
    console.error('[DISCREPANCY] Error fetching procurement:', procError);
    return 0;
  }

  const warehouseData = (procurement.procurement_warehouse as any)?.[0];
  const qualityData = (procurement.procurement_quality as any)?.[0];
  
  const receivedKg = warehouseData?.received_quantity_kg || procurement.total_quantity || 0;
  const compensationKg = qualityData?.compensation_kg || qualityData?.preliminary_compensation_kg || 0;
  const netPayableKg = receivedKg - compensationKg;
  // Calculate price per unit from total
  const pricePerUnit = (procurement.total_quantity && procurement.total_quantity > 0) 
    ? (procurement.total_amount || 0) / procurement.total_quantity 
    : 0;
  const totalNetPayable = netPayableKg * pricePerUnit;

  console.log('[DISCREPANCY] Calculated from procurement-level data:', {
    receivedKg,
    compensationKg,
    netPayableKg,
    pricePerUnit,
    totalNetPayable
  });

  return totalNetPayable;
}

/**
 * Check if a discrepancy exists and create one if needed
 */
export async function checkAndCreateDiscrepancy(
  procurementId: string,
  originalPaidAmount: number
): Promise<{ hasDiscrepancy: boolean; discrepancy?: ProcurementDiscrepancy }> {
  const finalCalculatedAmount = await calculateFinalNetPayable(procurementId);
  const discrepancyAmount = originalPaidAmount - finalCalculatedAmount;

  console.log('[DISCREPANCY] Checking discrepancy:', {
    procurementId,
    originalPaidAmount,
    finalCalculatedAmount,
    discrepancyAmount,
    threshold: DISCREPANCY_THRESHOLD
  });

  // If difference is below threshold, no discrepancy
  if (Math.abs(discrepancyAmount) <= DISCREPANCY_THRESHOLD) {
    return { hasDiscrepancy: false };
  }

  // Create discrepancy record
  const discrepancyData: CreateDiscrepancyInput = {
    procurement_id: procurementId,
    original_paid_amount: originalPaidAmount,
    final_calculated_amount: finalCalculatedAmount,
    discrepancy_amount: discrepancyAmount,
    discrepancy_type: discrepancyAmount > 0 ? 'overpayment' : 'underpayment',
  };

  const { data: createdDiscrepancy, error } = await supabase
    .from('procurement_discrepancies')
    .insert(discrepancyData)
    .select()
    .single();

  if (error) {
    console.error('[DISCREPANCY] Error creating discrepancy:', error);
    throw new Error(`Failed to create discrepancy record: ${error.message}`);
  }

  console.log('[DISCREPANCY] Created discrepancy:', createdDiscrepancy);

  return { 
    hasDiscrepancy: true, 
    discrepancy: createdDiscrepancy as ProcurementDiscrepancy 
  };
}

/**
 * Send Telegram notification for a new discrepancy
 */
export async function sendDiscrepancyNotification(
  procurementId: string,
  originalPaidAmount: number,
  finalCalculatedAmount: number,
  discrepancyAmount: number
): Promise<void> {
  try {
    // Get procurement details (polymorphic source — resolve name separately)
    const { data: procurement } = await supabase
      .from('procurements')
      .select(`id, product_name, source_type, source_id`)
      .eq('id', procurementId)
      .single();

    let supplierName = 'Unknown';
    if (procurement?.source_type && procurement?.source_id) {
      const map = await resolveSourceNames([
        { source_type: procurement.source_type, source_id: procurement.source_id },
      ]);
      supplierName =
        getSourceName(map, {
          source_type: procurement.source_type,
          source_id: procurement.source_id,
        }) || 'Unknown';
    }
    const productName = procurement?.product_name || 'Unknown Product';
    const discrepancyType = discrepancyAmount > 0 ? 'Overpaid' : 'Underpaid';
    const shortId = procurementId.substring(0, 8);

    const message = `⚠️ *Payment Discrepancy Detected*

📦 Procurement: \`PROC-${shortId}\`
🏢 Supplier: ${supplierName}
🥬 Product: ${productName}

💰 Originally Paid: Br ${originalPaidAmount.toLocaleString('en-ET', { minimumFractionDigits: 2 })}
📊 Final Net Payable: Br ${finalCalculatedAmount.toLocaleString('en-ET', { minimumFractionDigits: 2 })}
⚡ Discrepancy: Br ${Math.abs(discrepancyAmount).toLocaleString('en-ET', { minimumFractionDigits: 2 })} (${discrepancyType})

📍 Reason: Warehouse received quantities differed from estimate

Please review in Finance → Procurements → Discrepancies`;

    await sendTelegramNotification(message, procurementId);
    console.log('[DISCREPANCY] Telegram notification sent');
  } catch (error) {
    console.error('[DISCREPANCY] Failed to send Telegram notification:', error);
    // Don't throw - notification failure shouldn't block the main flow
  }
}

/**
 * Update a discrepancy status
 */
export async function updateDiscrepancyStatus(
  discrepancyId: string,
  updates: UpdateDiscrepancyInput
): Promise<ProcurementDiscrepancy> {
  const { data, error } = await supabase
    .from('procurement_discrepancies')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', discrepancyId)
    .select()
    .single();

  if (error) {
    console.error('[DISCREPANCY] Error updating discrepancy:', error);
    throw new Error(`Failed to update discrepancy: ${error.message}`);
  }

  return data as ProcurementDiscrepancy;
}

/**
 * Get discrepancy for a specific procurement
 */
export async function getDiscrepancyByProcurementId(
  procurementId: string
): Promise<ProcurementDiscrepancy | null> {
  const { data, error } = await supabase
    .from('procurement_discrepancies')
    .select(`
      *,
      procurements(
        id,
        product_name,
        total_quantity,
        created_at,
        source_type,
        source_id
      ),
      user_profiles!procurement_discrepancies_resolved_by_fkey(
        full_name,
        email
      )
    `)
    .eq('procurement_id', procurementId)
    .maybeSingle();

  if (error) {
    console.error('[DISCREPANCY] Error fetching discrepancy:', error);
    return null;
  }

  if (!data) return null;

  const proc: any = (data as any).procurements;
  let agent: any = null;
  let supplier: any = null;
  if (proc?.source_type && proc?.source_id) {
    const map = await resolveSourceNames([
      { source_type: proc.source_type, source_id: proc.source_id },
    ]);
    const name = getSourceName(map, {
      source_type: proc.source_type,
      source_id: proc.source_id,
    });
    if (name) {
      if (proc.source_type === 'agent') agent = { name };
      else if (proc.source_type === 'supplier') supplier = { name };
    }
  }

  return {
    ...data,
    procurement: proc
      ? {
          id: proc.id,
          product_name: proc.product_name,
          total_quantity: proc.total_quantity,
          created_at: proc.created_at,
          agent,
          supplier,
        }
      : undefined,
    resolver: data.user_profiles as any,
  } as ProcurementDiscrepancy;
}

/**
 * Get pending discrepancy count for dashboard
 */
export async function getPendingDiscrepancyStats(): Promise<{
  count: number;
  totalAmount: number;
}> {
  const { data, error } = await supabase
    .from('procurement_discrepancies')
    .select('discrepancy_amount')
    .eq('status', 'pending');

  if (error) {
    console.error('[DISCREPANCY] Error fetching pending stats:', error);
    return { count: 0, totalAmount: 0 };
  }

  const count = data?.length || 0;
  const totalAmount = data?.reduce((sum, d) => sum + Math.abs(d.discrepancy_amount), 0) || 0;

  return { count, totalAmount };
}
